/**
 * La déclaration d'activité de prestataire de formation, et la pièce qu'un
 * micro-entrepreneur doit y joindre.
 *
 * LA DÉCLARATION ELLE-MÊME NE SE FABRIQUE PAS ICI. Elle se dépose sur le
 * portail « Mon activité formation », ou sur le Cerfa 10782*06, qui n'a aucun
 * champ remplissable : c'est un imprimé à compléter à la main. Ce que
 * l'administration attend en plus d'un micro-entrepreneur, en revanche, est un
 * document que personne ne sait rédiger, et c'est celui-là que ce module
 * produit.
 *
 * LE MODÈLE EST FIXÉ PAR ARRÊTÉ, comme l'attestation au conjoint : arrêté du
 * 24 novembre 2025, pris pour le II de l'article R. 6351-5 du code du travail,
 * publié au Journal officiel du 27 novembre 2025. Ses huit rubriques, A à H,
 * sont reproduites au mot près, parenthèses comprises. Le micro-entrepreneur
 * dont le chiffre d'affaires ne dépasse pas le plafond de l'article 50-0 du
 * code général des impôts est dispensé de deux des pièces exigées des autres
 * déclarants, et remet cette présentation à la place.
 *
 * TROIS PORTES FERMÉES, et c'est la moitié du service rendu :
 *   - sans première convention ou premier contrat signé, il n'y a rien à
 *     déclarer : le délai de trois mois de l'article R. 6351-1 court à partir
 *     de cette signature, et la déclaration déposée avant est sans objet ;
 *   - au-dessus du plafond, le modèle simplifié ne s'applique pas, et c'est le
 *     dossier complet du I de l'article R. 6351-5 qu'il faut constituer ;
 *   - une activité qui n'est pas de la formation professionnelle continue ne
 *     se déclare pas du tout.
 *
 * ÉCART ASSUMÉ AVEC LE MODÈLE : l'annexe ne prévoit ni identification du
 * déclarant ni signature, parce qu'elle est pensée comme une pièce d'un dossier
 * qui les porte déjà. Une pièce jointe circule seule, et une présentation
 * anonyme ne se rattache à rien : le document produit porte donc le nom et le
 * SIRET en tête, et la place d'un paraphe en pied. Les huit rubriques, elles,
 * ne bougent pas d'un mot.
 */

import { PLAFONDS } from "./microEnterprise.js";
import { dateEnToutesLettres, identite, nettoyer } from "./documentText.js";

/** Le délai de dépôt de la déclaration, article R. 6351-1 du code du travail. */
export const DELAI_DEPOT_MOIS = 3;

/** Ce que l'administration a pour répondre, son silence valant enregistrement. */
export const DELAI_REPONSE_MOIS = 2;

/**
 * Le plafond sous lequel le modèle simplifié s'applique.
 *
 * Le II de R. 6351-5 renvoie au 2° du 1 de l'article 50-0 du code général des
 * impôts, c'est-à-dire au plafond des prestations de services : le lire dans
 * PLAFONDS plutôt que de le recopier, sans quoi il vieillira ici tout seul.
 */
export const PLAFOND_MODELE_SIMPLIFIE = PLAFONDS.services;

/** Les cinq publics de la rubrique D, dans l'ordre du modèle. */
export const PUBLICS = ["salarie", "alternant", "independant", "demandeurEmploi", "autres"];

/** Ce que le modèle écrit en face de chaque case de la rubrique D. */
const LIBELLE_PUBLIC = {
    salarie: "Salarié",
    alternant: "Alternant (y compris apprentis)",
    independant: "Indépendant",
    demandeurEmploi: "Demandeur d'emploi",
    autres: "Autres",
};

/** Ce que le modèle écrit en face de chaque case de la rubrique B. */
const LIBELLE_CONTRAT = {
    particulier: "contrat avec un particulier",
    entreprise: "convention avec une entreprise",
    sousTraitance: "contrat de sous-traitance",
};

/** Ce que le modèle propose en rubrique H. */
const LIBELLE_MODALITE = {
    presentiel: "présentiel",
    distanciel: "distanciel",
    mixte: "mixte",
};

/** Le SIREN d'un donneur d'ordre ou d'une entreprise cliente : neuf chiffres. */
export function siren(brut) {
    const n = nettoyer(brut).replace(/\s/g, "");
    if (!n) return { valeur: "", motif: "vide" };
    if (!/^\d{9}$/.test(n)) return { valeur: n, motif: "format" };
    return { valeur: n, motif: null };
}

/** Le SIRET du déclarant : quatorze chiffres, le SIREN plus le rang. */
export function siret(brut) {
    const n = nettoyer(brut).replace(/\s/g, "");
    if (!n) return { valeur: "", motif: "vide" };
    if (!/^\d{14}$/.test(n)) return { valeur: n, motif: "format" };
    return { valeur: n, motif: null };
}

/** Une date ISO découpée, ou null si elle n'en est pas une. */
function jour(iso) {
    const t = nettoyer(iso);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(t)) return null;
    const [a, m, j] = t.split("-").map(Number);
    const d = new Date(Date.UTC(a, m - 1, j));
    if (d.getUTCFullYear() !== a || d.getUTCMonth() !== m - 1 || d.getUTCDate() !== j) return null;
    return { a, m, j, date: d };
}

/**
 * La date limite de dépôt : trois mois après la signature.
 *
 * Un quantième qui n'existe pas dans le mois d'arrivée recule au dernier jour
 * de ce mois : le 30 novembre donne le 28 février, pas le 2 mars. C'est la
 * règle de computation des délais en mois, et l'oublier avance l'échéance d'un
 * ou deux jours, ce qui est exactement le genre d'erreur qu'on ne voit pas.
 */
export function dateLimiteDeDepot(dateSignature) {
    const d = jour(dateSignature);
    if (!d) return null;
    const cible = new Date(Date.UTC(d.a, d.m - 1 + DELAI_DEPOT_MOIS, d.j));
    if (cible.getUTCDate() !== d.j) cible.setUTCDate(0);
    return cible.toISOString().slice(0, 10);
}

/** Où en est le délai de dépôt, au jour dit. */
export function etatDuDelai(dateSignature, aujourdhui) {
    const limite = dateLimiteDeDepot(dateSignature);
    const maintenant = jour(String(aujourdhui).slice(0, 10));
    const signature = jour(dateSignature);
    if (!limite || !maintenant || !signature) return null;

    const fin = jour(limite);
    const jours = Math.round((fin.date - maintenant.date) / 86400000);
    return {
        limite,
        jours,
        depasse: jours < 0,
        // Une signature future n'est pas une faute de saisie impossible, mais
        // elle ne fait pas courir de délai : on le dit plutôt que de compter.
        aVenir: signature.date > maintenant.date,
    };
}

/** Le nombre de personnes formées, toutes lignes de la rubrique D confondues. */
export function totalPublic(reponses) {
    return PUBLICS.reduce((total, cle) => {
        const n = Number(nettoyer(String(reponses?.[`public_${cle}`] ?? "")));
        return total + (Number.isInteger(n) && n > 0 ? n : 0);
    }, 0);
}

/** Les lignes de la rubrique D réellement renseignées, dans l'ordre du modèle. */
export function publicsRenseignes(reponses) {
    return PUBLICS.filter((cle) => {
        const n = Number(nettoyer(String(reponses?.[`public_${cle}`] ?? "")));
        return Number.isInteger(n) && n > 0;
    });
}

/**
 * Qui doit déposer une déclaration, et qui n'a rien à déposer.
 *
 * Le tri écarte trois demandeurs sur lesquels ce document ne peut rien : celui
 * qui n'exerce pas d'action de formation professionnelle, celui qui n'a pas
 * encore signé, et celui que son chiffre d'affaires sort du modèle simplifié.
 */
export function concerne(reponses) {
    const r = reponses || {};
    if (!r.nature) return { concerne: null, motif: null };
    if (r.nature === "horsFormation") return { concerne: false, motif: "horsFormation" };
    if (!r.contratSigne) return { concerne: null, motif: null };
    if (r.contratSigne === "pasEncore") return { concerne: false, motif: "pasDeContrat" };
    if (!r.sousLePlafond) return { concerne: null, motif: null };
    if (r.sousLePlafond === "non") return { concerne: false, motif: "auDessusDuPlafond" };
    return { concerne: true, motif: null };
}

/** Ce qui manque, ce qui est refusé, et ce qui mérite un mot. */
export function valider(reponses, aujourdhui) {
    const r = reponses || {};
    const manques = [];
    const invalides = [];
    const alertes = [];

    if (!concerne(r).concerne) {
        return { valide: false, manques: ["nature"], invalides, alertes };
    }

    const numero = siret(r.siret);
    if (numero.motif === "vide") manques.push("siret");
    else if (numero.motif) invalides.push({ champ: "siret", motif: numero.motif });

    for (const cle of ["prenoms", "nom", "objectif", "formateur", "moyens", "lieuDeSignature"]) {
        if (!nettoyer(r[cle])) manques.push(cle);
    }

    /* Rubrique A : la date de signature, dont tout le délai découle. */
    if (!nettoyer(r.dateSignature)) manques.push("dateSignature");
    else if (!jour(r.dateSignature)) invalides.push({ champ: "dateSignature", motif: "format" });
    else {
        const delai = etatDuDelai(r.dateSignature, aujourdhui);
        if (delai?.aVenir) invalides.push({ champ: "dateSignature", motif: "aVenir" });
        else if (delai?.depasse) alertes.push("delaiDepasse");
    }

    /* Rubrique B : le SIREN n'est demandé que par deux des trois cases. */
    if (!r.typeContrat) manques.push("typeContrat");
    else if (r.typeContrat !== "particulier") {
        const donneur = siren(r.sirenPartenaire);
        if (donneur.motif === "vide") manques.push("sirenPartenaire");
        else if (donneur.motif) invalides.push({ champ: "sirenPartenaire", motif: donneur.motif });
    }

    /* Rubrique C : la date prévue de l'action. */
    if (!nettoyer(r.dateAction)) manques.push("dateAction");
    else if (!jour(r.dateAction)) invalides.push({ champ: "dateAction", motif: "format" });

    /* Rubrique D : au moins un public, et un nombre entier positif partout. */
    const renseignes = publicsRenseignes(r);
    if (renseignes.length === 0) manques.push("publics");
    for (const cle of PUBLICS) {
        const brut = nettoyer(String(r[`public_${cle}`] ?? ""));
        if (!brut) continue;
        if (!/^\d{1,4}$/.test(brut) || Number(brut) <= 0) {
            invalides.push({ champ: "publics", motif: "nombre", ligne: cle });
        }
    }
    if (renseignes.includes("autres") && !nettoyer(r.autresPrecision)) manques.push("autresPrecision");

    /* Rubrique H : l'accompagnement n'est exigé que si la formation est à distance. */
    if (!r.modalite) manques.push("modalite");
    else if (r.modalite !== "presentiel" && !nettoyer(r.accompagnement)) manques.push("accompagnement");

    const brut = nettoyer(r.dateDeSignature);
    if (!brut) manques.push("dateDeSignature");
    else if (!dateEnToutesLettres(brut)) invalides.push({ champ: "dateDeSignature", motif: "format" });

    return {
        valide: manques.length === 0 && invalides.length === 0,
        manques: [...new Set(manques)],
        invalides,
        alertes,
    };
}

/** La rubrique B, écrite comme le modèle la propose, case tranchée. */
function ligneTypeDeContrat(r) {
    const type = LIBELLE_CONTRAT[r.typeContrat] || "";
    if (r.typeContrat === "entreprise") {
        return `${type} (SIREN de l'entreprise : ${siren(r.sirenPartenaire).valeur})`;
    }
    if (r.typeContrat === "sousTraitance") {
        return `${type} (SIREN du donneur d'ordre : ${siren(r.sirenPartenaire).valeur})`;
    }
    return type;
}

/** La rubrique D, une ligne par public renseigné. */
function lignesDuPublic(r) {
    return publicsRenseignes(r).map((cle) => {
        const nombre = nettoyer(String(r[`public_${cle}`]));
        const libelle =
            cle === "autres"
                ? `${LIBELLE_PUBLIC.autres} (${nettoyer(r.autresPrecision)})`
                : LIBELLE_PUBLIC[cle];
        return `${libelle} : ${nombre}`;
    });
}

/**
 * La présentation de l'activité, en blocs, prête pour le papier comme pour le PDF.
 *
 * Les huit libellés viennent de l'annexe de l'arrêté et ne se reformulent pas :
 * c'est à ce texte-là que l'agent de la DREETS compare la pièce reçue.
 */
export function composerPresentation(reponses, aujourdhui) {
    const r = reponses || {};
    const dateDeSignature = nettoyer(r.dateDeSignature) || String(aujourdhui).slice(0, 10);
    const complet = { ...r, dateDeSignature };
    if (!valider(complet, aujourdhui).valide) return null;

    const nomComplet = identite({ prenoms: r.prenoms, nom: r.nom });
    const distance = r.modalite === "distanciel" || r.modalite === "mixte";

    return {
        titre: "Présentation de l'activité de formation du micro-entrepreneur",
        blocs: [
            {
                type: "paragraphe",
                texte:
                    "Modèle fixé par l'arrêté du 24 novembre 2025, pris pour l'application du II " +
                    "de l'article R. 6351-5 du code du travail.",
            },
            {
                type: "lignes",
                lignes: [`${nomComplet}, micro-entrepreneur`, `SIRET : ${siret(r.siret).valeur}`],
            },
            {
                type: "paragraphe",
                texte:
                    "A. - Date de signature du premier contrat ou de la première convention de " +
                    `formation professionnelle (moins de trois mois) : ${dateEnToutesLettres(r.dateSignature)}.`,
            },
            { type: "paragraphe", texte: `B. - Type de contrat : ${ligneTypeDeContrat(r)}.` },
            {
                type: "paragraphe",
                texte: `C. - Date prévue de l'action de formation : ${dateEnToutesLettres(r.dateAction)}.`,
            },
            { type: "lignes", lignes: ["D. - Public formé (précisez le nombre) :", ...lignesDuPublic(r)] },
            {
                type: "paragraphe",
                texte:
                    "E. - Objectif professionnel de l'action réalisée (compétences professionnelles " +
                    `attendues, éventuellement la certification professionnelle visée) : ${nettoyer(r.objectif)}`,
            },
            {
                type: "paragraphe",
                texte:
                    "F. - Titres et qualités (expérience, etc.) du formateur en lien avec l'action " +
                    `réalisée : ${nettoyer(r.formateur)}`,
            },
            {
                type: "paragraphe",
                texte:
                    "G. - Moyens techniques et pédagogiques mobilisés (livret d'apprentissage, " +
                    `support de formation…) : ${nettoyer(r.moyens)}`,
            },
            {
                type: "paragraphe",
                texte:
                    `H. - Modalités de déroulement : ${LIBELLE_MODALITE[r.modalite]}.` +
                    (distance ? ` Modalités d'accompagnement technique et pédagogique à distance : ${nettoyer(r.accompagnement)}` : ""),
            },
            {
                type: "paragraphe",
                texte: `À ${nettoyer(r.lieuDeSignature)}, le ${dateEnToutesLettres(dateDeSignature)}.`,
            },
            { type: "signature", nom: nomComplet },
        ],
    };
}

/** Ce que le parcours annonce avant le téléchargement. */
export function recapitulatif(reponses, aujourdhui) {
    const r = reponses || {};
    if (!composerPresentation(r, aujourdhui)) return null;
    const delai = etatDuDelai(r.dateSignature, aujourdhui);

    return [
        ["Déclarant", `${identite({ prenoms: r.prenoms, nom: r.nom })}, SIRET ${siret(r.siret).valeur}`],
        ["Premier contrat signé le", dateEnToutesLettres(r.dateSignature)],
        ["Dépôt attendu avant le", dateEnToutesLettres(delai.limite)],
        ["Personnes formées", String(totalPublic(r))],
        ["Réponse de l'administration", `sous ${DELAI_REPONSE_MOIS} mois, son silence valant enregistrement`],
    ];
}
